// formatValue.ts — physical-quantity formatter (ADR-030).
//
// The DOMAIN layer of the two-layer split: it decides *which unit* and *how
// many decimals* a quantity should display in, then delegates the actual
// locale painting (digits, separators, range separator) to format.ts. SI unit
// symbols (kW, MWh) are appended here as locale-invariant Latin text.
//
// A dimension is DATA, not code: the registry below describes each quantity;
// one generic scaler reads it. Adding a unit/tier is a registry row, never a
// new branch. The off-the-shelf unit libraries don't fit our compound
// electrical units (kWh/kVA/Ah) and add a second locale system, so this stays
// hand-rolled (ADR-030).
//
// scale-vs-pin policy (ADR-030): single values auto-scale to the readable tier;
// table columns / chart axes / ranges PIN to one fixed tier (value-only, unit
// in the header). formatRange pins by the MAX end.

import { formatNumber, formatNumberRange } from './format.js';

// ── tiers + registry ───────────────────────────────────────────────────────

// A display tier: the stored value DIVIDED by `factor` gives the displayed
// number. The stored (canonical) unit always has factor 1.
interface Tier {
  symbol: string;
  factor: number;
}

interface DimensionDef {
  /** Ascending by factor. First entry with factor 1 is the stored unit. */
  tiers: Tier[];
  /** Max fraction digits (trailing zeros stripped). */
  maxFractionDigits: number;
  /** Thousands grouping (default true; false for coordinates). */
  useGrouping?: boolean;
  /** Space between number and unit. SI units → true; %/° → false. */
  unitSpace?: boolean;
}

// SI decimal prefixes (symbol + multiplier relative to the unprefixed base).
const SI_PREFIXES = [
  { symbol: '', value: 1 },
  { symbol: 'k', value: 1e3 },
  { symbol: 'M', value: 1e6 },
  { symbol: 'G', value: 1e9 },
  { symbol: 'T', value: 1e12 },
] as const;

/**
 * Generate an SI ×1000 tier ladder from an unprefixed base symbol. `storedPrefix`
 * marks which prefixed form is the canonical DB unit (factor 1). e.g.
 * `siTiers('W', 'k', '', 'G')` → W·kW·MW·GW with kW at factor 1.
 */
function siTiers(base: string, storedPrefix: string, from: string, to: string): Tier[] {
  const stored = SI_PREFIXES.find((p) => p.symbol === storedPrefix)!.value;
  const fromIdx = SI_PREFIXES.findIndex((p) => p.symbol === from);
  const toIdx = SI_PREFIXES.findIndex((p) => p.symbol === to);
  const tiers: Tier[] = [];
  for (let i = fromIdx; i <= toIdx; i++) {
    const p = SI_PREFIXES[i]!;
    tiers.push({ symbol: p.symbol + base, factor: p.value / stored });
  }
  return tiers;
}

// The seed registry (ADR-030). Stored units mirror CONVENTIONS §Number & unit
// display. Add a dimension as one row when its field/feature ships.
const REGISTRY = {
  // ── scaling (si-decimal) ──
  power: { tiers: siTiers('W', 'k', '', 'G'), maxFractionDigits: 2 }, // W·kW·MW·GW
  apparent_power: { tiers: siTiers('VA', 'k', '', 'G'), maxFractionDigits: 2 }, // VA·kVA·MVA·GVA
  reactive_power: { tiers: siTiers('var', 'k', '', 'G'), maxFractionDigits: 2 }, // var·kvar·Mvar·Gvar
  energy: { tiers: siTiers('Wh', 'k', '', 'T'), maxFractionDigits: 2 }, // Wh·kWh·MWh·GWh·TWh
  // explicit tiers — `t`/`kt` are conventional symbols, not SI-prefixed `g`.
  co2_mass: {
    tiers: [
      { symbol: 'kg', factor: 1 },
      { symbol: 't', factor: 1e3 },
      { symbol: 'kt', factor: 1e6 },
    ],
    maxFractionDigits: 2,
  },
  // ── single-tier (no scaling) ──
  fuel_volume: { tiers: [{ symbol: 'L', factor: 1 }], maxFractionDigits: 0 }, // "12 569 L"
  duration_h: { tiers: [{ symbol: 'h', factor: 1 }], maxFractionDigits: 1 },
  battery_charge: { tiers: [{ symbol: 'Ah', factor: 1 }], maxFractionDigits: 0 },
  frequency: { tiers: [{ symbol: 'Hz', factor: 1 }], maxFractionDigits: 2 },
  // percent: plain number + '%' (no Intl style:'percent' — avoids the ×100
  // footgun on a 0–100 stored value). No space (ADR-030).
  ratio_pct: { tiers: [{ symbol: '%', factor: 1 }], maxFractionDigits: 2, unitSpace: false },
  count: { tiers: [{ symbol: '', factor: 1 }], maxFractionDigits: 0 },
  coordinate: {
    tiers: [{ symbol: '', factor: 1 }],
    maxFractionDigits: 6,
    useGrouping: false,
  },
} satisfies Record<string, DimensionDef>;

export type Dimension = keyof typeof REGISTRY;

// ── output shape ───────────────────────────────────────────────────────────

export interface FormattedValue {
  /** Combined string — e.g. "1.5 MW" (value + unit). */
  display: string;
  /** Number part only — for table cells where the unit is in the header. */
  value: string;
  /** Unit symbol only — for column headers. */
  unit: string;
  /** Original numeric value (coerced). */
  raw: number;
}

// ── scaling ──────────────────────────────────────────────────────────────

const storedTier = (def: DimensionDef): Tier => def.tiers.find((t) => t.factor === 1) ?? def.tiers[0]!;

/** Pick the tier so |value/factor| lands in [1, 1000); 0 → the stored unit. */
function pickTier(absValue: number, def: DimensionDef): Tier {
  if (absValue === 0) return storedTier(def);
  for (let i = def.tiers.length - 1; i >= 0; i--) {
    if (absValue / def.tiers[i]!.factor >= 1) return def.tiers[i]!;
  }
  return def.tiers[0]!; // smaller than the smallest tier
}

/**
 * Scale a stored value to its display tier. `fixedUnit` pins to a named tier
 * (table columns / ranges) instead of auto-scaling.
 * Re-picks a higher tier if rounding pushes the mantissa to 1000.
 */
function scaleToTier(value: number, def: DimensionDef, fixedUnit?: string): Tier {
  if (fixedUnit !== undefined) {
    return def.tiers.find((t) => t.symbol === fixedUnit) ?? storedTier(def);
  }
  let tier = pickTier(Math.abs(value), def);
  // boundary re-pick: 999.96 kW @ 2dp would render "1,000.00" — bump to MW.
  const rounded = Math.abs(Number((value / tier.factor).toFixed(def.maxFractionDigits)));
  if (rounded >= 1000) {
    const higher = def.tiers.find((t) => t.factor === tier.factor * 1000);
    if (higher) tier = higher;
  }
  return tier;
}

function coerce(value: number | string | null | undefined): number | null {
  if (value === null || value === undefined) return null;
  const n = typeof value === 'string' ? parseFloat(value) : value;
  return Number.isNaN(n) ? null : n;
}

function withUnit(numberStr: string, unit: string, def: DimensionDef): string {
  if (!unit) return numberStr;
  return `${numberStr}${def.unitSpace === false ? '' : ' '}${unit}`;
}

// ── public API ─────────────────────────────────────────────────────────────

/**
 * Format a single physical quantity, auto-scaled to the readable tier.
 * Returns null for null/undefined/non-numeric input (caller renders —).
 * Pass `fixedUnit` to pin (table cell at the column's unit).
 */
export function formatValue(
  value: number | string | null | undefined,
  dimension: Dimension,
  fixedUnit?: string,
): FormattedValue | null {
  const num = coerce(value);
  if (num === null) return null;

  const def: DimensionDef = REGISTRY[dimension];
  const tier = scaleToTier(num, def, fixedUnit);
  const valueStr = formatNumber(num / tier.factor, {
    maximumFractionDigits: def.maxFractionDigits,
    useGrouping: def.useGrouping ?? true,
  });

  return {
    display: withUnit(valueStr, tier.symbol, def),
    value: valueStr,
    unit: tier.symbol,
    raw: num,
  };
}

/**
 * Format a min–max range with a single coherent unit. Pins by the MAX end
 * (ADR-030) so the larger number reads cleanly; both ends scale to that tier.
 * Returns null if either end is null/undefined/non-numeric.
 */
export function formatBandRange(
  min: number | string | null | undefined,
  max: number | string | null | undefined,
  dimension: Dimension,
): string | null {
  const minNum = coerce(min);
  const maxNum = coerce(max);
  if (minNum === null || maxNum === null) return null;

  const def: DimensionDef = REGISTRY[dimension];
  // Pin by the larger-magnitude end so its mantissa lands in [1, 1000).
  const pivot = Math.abs(maxNum) >= Math.abs(minNum) ? maxNum : minNum;
  const tier = scaleToTier(pivot, def);
  const rangeStr = formatNumberRange(minNum / tier.factor, maxNum / tier.factor, {
    maximumFractionDigits: def.maxFractionDigits,
    useGrouping: def.useGrouping ?? true,
  });
  return withUnit(rangeStr, tier.symbol, def);
}
